import { useMemo } from 'react';
import sortBy from 'lodash/sortBy';

import {
  SettingSmallText,
  SettingsGroup,
  SettingsHr,
  SettingsLabel,
} from 'approot/activity-builder/settings/settings.style';
import { ScreenReaderOnly } from 'approot/shared/styles/global.styles';
import { SettingsProps } from './multiple-choice-settings.constants';
import {
  ANSWER_LABELS,
  CORRECT,
  INCORRECT,
} from '../multiple-choice.constants';
import Switch from 'approot/shared/switch/switch';
import { MoreInformationCheckAnswerTooltip } from '../../shared/more-information/more-information-toolip';
import { Box, BoxList } from 'approot/shared/design-system/box/box.style';
import {
  SortableContent,
  SortableItem,
} from 'approot/shared/sortable/sortable.style';
import { PStyled } from 'approot/shared/styles/typography.style';
import { CheckableSingle } from 'approot/shared/styles/buttons/checkable-input';
import { ImageThumbnailStatic } from 'approot/shared/images/images';
import {
  ACTIVITIES_ANSWER_MODE_FIXED,
  ACTIVITIES_ANSWER_MODE_FREE,
  ACTIVITIES_RESPONSE_VARIANT_IMAGE,
  ACTIVITIES_RESPONSE_VARIANT_TEXT,
  ACTIVITIES_RESPONSE_VARIANT_TEXT_IMAGE,
  LANGUAGE_CODE_EN_AU,
  LESSON_ACTIVITIES,
} from 'global/constants';
import { useMultipleChoiceUpdate } from './multiple-choice-settings.hooks';
import { useGetInteractiveLessonParams } from 'approot/activity-builder/activity-builder.hooks';
import { ComponentResponseTextArea } from '../../shared/component-parts.style';
import IconExclamation from 'approot/shared/icons/icon-exclamation';
import { red9 } from 'approot/shared/styles/colours.style';

const MORE_INFORMATION_FIELD = 'moreInformationField';

const MultipleChoiceSettingsSolution = ({
  sectionId,
  id,
  type,
  sort,
  properties,
  subActivityProperties,
  suggestedAnswerProperties,
  version,
}: SettingsProps) => {
  const { contentType } = useGetInteractiveLessonParams();

  const isLesson = contentType === LESSON_ACTIVITIES;

  const { handleUpdate } = useMultipleChoiceUpdate({
    id,
    sectionId,
    type,
    sort,
    properties,
    subActivityProperties,
    suggestedAnswerProperties,
    language: LANGUAGE_CODE_EN_AU,
    version,
  });

  const { options, answerMode, isFreeAnswerMultiSelect } =
    properties.multipleChoice;
  const sortedOptions = useMemo(() => sortBy(options, 'sort'), [options]);

  const isImage =
    properties.multipleChoice.variant === ACTIVITIES_RESPONSE_VARIANT_IMAGE;
  const isText =
    properties.multipleChoice.variant === ACTIVITIES_RESPONSE_VARIANT_TEXT;
  const isTextAndImage =
    properties.multipleChoice.variant ===
    ACTIVITIES_RESPONSE_VARIANT_TEXT_IMAGE;
  const isShowLetters = properties.multipleChoice.showOptionLetter;

  const hasCorrectAnswer = useMemo(
    () => options.some(option => option.correctAnswerBoolean),
    [options]
  );

  return (
    <SettingsGroup as="fieldset" marginBottom={0}>
      <ScreenReaderOnly as="legend">
        Multiple Choice Settings Solution
      </ScreenReaderOnly>
      <SettingsGroup>
        <SettingsLabel>Answer Type</SettingsLabel>
        <CheckableSingle
          name="answerMode"
          selected={answerMode || ACTIVITIES_ANSWER_MODE_FIXED}
          options={[
            {
              label: 'Fixed',
              value: ACTIVITIES_ANSWER_MODE_FIXED,
            },
            {
              label: 'Free',
              value: ACTIVITIES_ANSWER_MODE_FREE,
            },
          ]}
          onSelect={answerMode => {
            handleUpdate(
              {
                ...properties,
                multipleChoice: {
                  ...properties.multipleChoice,
                  answerMode,
                },
              },
              { clearSuggestedAnswers: true }
            );
          }}
        />
        {answerMode === ACTIVITIES_ANSWER_MODE_FIXED && (
          <SettingSmallText marginBottom={0}>
            There is a correct answer or answers.
          </SettingSmallText>
        )}
        {answerMode === ACTIVITIES_ANSWER_MODE_FREE && (
          <SettingSmallText marginBottom={0}>
            There is no predetermined answer and students are encouraged to
            provide their own responses based on their creativity and
            understanding.
          </SettingSmallText>
        )}
      </SettingsGroup>

      {answerMode === ACTIVITIES_ANSWER_MODE_FREE && (
        <SettingsGroup>
          <SettingsLabel>
            Allow students to select multiple options
          </SettingsLabel>
          <Switch
            state={!!isFreeAnswerMultiSelect}
            ariaLabel="Allow students to select multiple options"
            onClick={() =>
              handleUpdate({
                ...properties,
                multipleChoice: {
                  ...properties.multipleChoice,
                  isFreeAnswerMultiSelect: !isFreeAnswerMultiSelect,
                },
              })
            }
          />
        </SettingsGroup>
      )}

      {(!answerMode || answerMode === ACTIVITIES_ANSWER_MODE_FIXED) && (
        <>
          <SettingsGroup>
            <PStyled marginBottom={!hasCorrectAnswer ? 4 : 16}>
              Select the correct answer(s) below
            </PStyled>
            {!hasCorrectAnswer && (
              <Box $alignItems="center" $gap={4} $marginBottom={16}>
                <IconExclamation width={16} height={16} />
                <SettingSmallText marginBottom={0} colour={red9}>
                  Missing a correct answer.
                </SettingSmallText>
              </Box>
            )}
            <BoxList>
              {sortedOptions.map((item, index) => (
                <SortableItem
                  as="li"
                  key={item.itemId}
                  data-selector="choices-list"
                >
                  <SortableContent>
                    <Box $alignItems="center">
                      {(isImage || isTextAndImage) && (
                        <Box $marginRight={12}>
                          <ImageThumbnailStatic
                            imageUrl={item.image?.original || ''}
                            width={48}
                          />
                        </Box>
                      )}
                      <PStyled marginBottom={0}>
                        {isShowLetters && (
                          <strong>{ANSWER_LABELS[index]}</strong>
                        )}{' '}
                        {(isText || isTextAndImage) &&
                          (item.text && item.text.length > 0
                            ? item.text
                            : `Choice ${index + 1}`)}
                      </PStyled>
                    </Box>
                  </SortableContent>
                  <SettingsHr $marginBottom={0} />
                  <SortableContent>
                    <CheckableSingle
                      data-selector={`answer-choice-${index}`}
                      isSmall
                      name={`correctAnswer${index}`}
                      options={[
                        {
                          label: 'Correct',
                          value: CORRECT,
                        },
                        {
                          label: 'Incorrect',
                          value: INCORRECT,
                        },
                      ]}
                      selected={item.correctAnswerBoolean ? CORRECT : INCORRECT}
                      onSelect={value =>
                        handleUpdate({
                          ...properties,
                          multipleChoice: {
                            ...properties.multipleChoice,
                            options: options.map(option => {
                              if (option.itemId === item.itemId) {
                                return {
                                  ...option,
                                  correctAnswerBoolean: value === CORRECT,
                                };
                              }
                              return option;
                            }),
                          },
                        })
                      }
                    />
                  </SortableContent>
                </SortableItem>
              ))}
            </BoxList>
          </SettingsGroup>

          {isLesson && (
            <>
              <SettingsHr />
              <SettingsGroup>
                <SettingsLabel htmlFor={MORE_INFORMATION_FIELD}>
                  Add a Hint
                  <MoreInformationCheckAnswerTooltip />
                </SettingsLabel>

                <ComponentResponseTextArea
                  id={MORE_INFORMATION_FIELD}
                  name={MORE_INFORMATION_FIELD}
                  placeholder="Type here..."
                  initialValue={properties.multipleChoice.moreInformation || ''}
                  onChange={e =>
                    handleUpdate({
                      ...properties,
                      multipleChoice: {
                        ...properties.multipleChoice,
                        moreInformation: e.target.value,
                      },
                    })
                  }
                  data-selector="txt-more-information"
                />
              </SettingsGroup>
            </>
          )}
        </>
      )}
    </SettingsGroup>
  );
};

export default MultipleChoiceSettingsSolution;
